import React from "react";
import { Select, Pagination } from "antd";
import type { TablePaginationConfig } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";

import RebetLoader from "../CommonComponents/RebetLoader/RebetLoader";
import DataTableBase from "../shared/dashboards/DataTable/DataTable";
import { AppStrings } from "../../utils/constants/AppStrings";
import { PAGE_SIZE_OPTIONS } from "./reportingSummaryConstants";
import type { ReportingSummaryRow } from "./reportingSummaryTypes";

/** JS DataTable; typed columns from Ant Design do not match inferred `never[]` props. */
const DataTable = DataTableBase as React.ComponentType<Record<string, unknown>>;

export type ReportingSummaryTableOnChange = (
  pagination: TablePaginationConfig,
  filters: Record<string, FilterValue | null>,
  sorter:
    | SorterResult<ReportingSummaryRow>
    | SorterResult<ReportingSummaryRow>[],
) => void;

export interface ReportingSummaryTableSectionProps {
  columns: ColumnsType<ReportingSummaryRow>;
  dataSource: ReportingSummaryRow[];
  totalRowCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (value: unknown) => void;
  loading?: boolean;
  onTableChange?: ReportingSummaryTableOnChange;
}

function ReportingSummaryTableSection({
  columns,
  dataSource,
  totalRowCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  loading = false,
  onTableChange,
}: ReportingSummaryTableSectionProps) {
  if (loading) {
    return (
      <div className="reporting-summary-table-section">
        <div className="reporting-summary-loader">
          <RebetLoader height="100px" width="100px" />
        </div>
      </div>
    );
  }

  return (
    <div className="reporting-summary-table-section">
      <DataTable
        id="reporting-summary-table"
        rowKey="key"
        className="reporting-summary-table"
        columns={columns}
        dataSource={dataSource}
        rowClassName={(_record: ReportingSummaryRow, index: number) =>
          index % 2 === 1 ? "reporting-summary-table-row--alt" : ""
        }
        pagination={false}
        onChange={onTableChange}
        showSorterTooltip={{ target: "sorter-icon" }}
        scrollPreset="verticalOnly"
      />
      <div className="reporting-summary-pagination-bar">
        <div className="reporting-summary-pagination-left">
          <Select
            value={pageSize}
            onChange={onPageSizeChange}
            className="reporting-summary-page-size-select"
            options={[...PAGE_SIZE_OPTIONS].map((n) => ({
              value: n,
              label: String(n),
            }))}
          />
          <span className="reporting-summary-per-page-label">
            {AppStrings.REPORTING_PAGINATION_PER_PAGE}
          </span>
        </div>
        <div className="reporting-summary-pagination-right">
          <Pagination
            current={currentPage}
            total={totalRowCount}
            pageSize={pageSize}
            showSizeChanger={false}
            onChange={onPageChange}
            itemRender={(page, type, originalElement) => {
              if (type === "prev") {
                return (
                  <span className="reporting-summary-pagination-nav">
                    {originalElement}
                    <span>{AppStrings.BACK}</span>
                  </span>
                );
              }
              if (type === "next") {
                return (
                  <span className="reporting-summary-pagination-nav">
                    <span>{AppStrings.NEXT}</span>
                    {originalElement}
                  </span>
                );
              }
              return originalElement;
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default ReportingSummaryTableSection;
